// src/views/accueil/ControleursFeuilles.jsx — LA LOGIQUE derrière les feuilles
// du prototype : données réelles, états, écritures. Le DESSIN vit dans
// proto/FeuillesAccueil.jsx (transcription du fichier livré) ; ici on ne pose
// ni taille ni couleur — seulement des props.
//
// ⚠️ APRÈS CHAQUE DÉCISION (règle du 26/08) : la feuille se ferme SEULE, la
// ligne disparaît SANS rechargement (retrait optimiste via onSortir), et un
// refetch silencieux réconcilie derrière. Un refus du serveur RESTE dans la
// feuille et dit pourquoi — rien ne se ferme sur un échec.

import { useState, useEffect, useCallback, useMemo, useRef } from 'react'
import { supabase } from '../../supabase'
import Feuille from './Feuille'
import PortailFeuille from '../../components/PortailFeuille'
import ControleView from '../menage/ControleView'
import {
  FeuilleMenageSansAgent, FeuilleDemandeHoraire, FeuilleRefusHoraire,
  FeuilleJugement, FeuilleSuivi, FeuilleReprise, FeuilleControleSansPersonne,
} from './proto/FeuillesAccueil'
import { FeuilleFilMessage, FeuilleTaches, FeuilleProspects,
  FeuilleMenageDetail } from './proto/FeuillesComplement'
import { fetchLogInfo } from '../../lib/menage-log-info-cache'
import { groupPhotosByPiece } from '../../lib/menage-run-restitution'
import { fetchControleDetail, deciderReprise } from '../../lib/controle-detail-api'
import { designerControleurRun } from '../../lib/panneau-menage-api'
import { trancherQuiPaie, lirePrixArticle, retenirPrixArticle,
  createIncident, STATUS as STATUT_INCIDENT } from '../../lib/incidents-api'
import { uid } from '../../lib/format'
import { articlesATrancher, eurosCourt, ETAT } from '../../lib/accueil-incident'
import { jourCourt, heureCourte } from '../../lib/accueil-attend'

const Erreur = ({ texte }) => (texte
  ? <p className="dang" role="alert" data-testid="feuille-decision-erreur"
      style={{ marginTop: '10px' }}>{texte}</p>
  : null)

async function postAction(body) {
  const { data: { session } } = await supabase.auth.getSession()
  if (!session?.access_token) throw new Error('Session expirée')
  const res = await fetch('/api/beds24-mapping', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${session.access_token}` },
    body: JSON.stringify(body),
  })
  const json = await res.json().catch(() => ({}))
  if (!res.ok) throw new Error(json?.error || `Erreur ${res.status}`)
  return json
}

const prenomDe = (email) => {
  const e = typeof email === 'string' ? email.trim() : ''
  if (!e) return ''
  const p = e.split('@')[0]
  return p.charAt(0).toUpperCase() + p.slice(1)
}

/** Le roster proto : {id,nom,ini,charge,occupe,tagOccupe}. */
export function rosterDepuisOptions(options) {
  return options.map((o) => ({
    id: o.value, nom: o.label, ini: o.label.slice(0, 2).toUpperCase(),
    charge: o.charge || '—', occupe: !!o.occupe,
    tagOccupe: o.occupe ? 'occupé' : null,
  }))
}

/* ═══ 1 · UN MÉNAGE SANS AGENT ═════════════════════════════════════ */
export function CtrlSansAgent({ ligne, creneau, optionsAgents, currentUser,
  onFermer, onDecide, onRafraichir }) {
  const [choisi, setChoisi] = useState(null)
  const [erreur, setErreur] = useState(null)
  const [envoi, setEnvoi] = useState(false)
  const moiEmail = currentUser && currentUser.email

  const confier = useCallback(async () => {
    if (!choisi || envoi) return
    setEnvoi(true); setErreur(null)
    // ⚠️ LA GARDE D'ÉCRASEMENT (correction 27/08) : on n'assigne QUE si le
    // ménage est ENCORE sans agent — deux personnes qui désignent en même
    // temps, ça arrive. Rowcount 0 = quelqu'un est passé avant : la feuille
    // le dit, l'écran se recharge, rien n'est écrasé.
    const { data, error } = await supabase.from('menage_runs')
      .update({ assigned_provider_email: choisi })
      .eq('id', ligne.runId).is('assigned_provider_email', null).select('id')
    setEnvoi(false)
    if (error || !data || data.length === 0) {
      setErreur("Quelqu'un vient d'assigner ce ménage — l'écran se met à jour.")
      onRafraichir && onRafraichir()
      return
    }
    onFermer(); onDecide(ligne.cle)
  }, [choisi, envoi, ligne, onFermer, onDecide, onRafraichir])

  return (
    <Feuille testId="accueil-feuille-menage" titre="Un ménage sans agent" onFermer={onFermer}
      enfants={
        <div data-testid="feuille-designer-agent">
          <FeuilleMenageSansAgent
            sousTitre={ligne.sousTitre}
            departH={creneau ? creneau.departH : '—'} departPlein={!!(creneau && creneau.departSur)}
            arriveeH={creneau ? creneau.arriveeH : '—'} arriveePleine={!!(creneau && creneau.arriveeSur)}
            dureeTxt={creneau && creneau.duree}
            agents={rosterDepuisOptions(optionsAgents)}
            moi={moiEmail ? { id: moiEmail, nom: 'Moi' } : null}
            choisi={choisi} onChoisir={(id) => setChoisi(choisi === id ? null : id)}
            onConfier={confier} onAnnuler={onFermer}
            erreur={<Erreur texte={erreur} />} />
        </div>
      } />
  )
}

/* ═══ 2 · UNE DEMANDE D'HORAIRE ════════════════════════════════════ */
export function CtrlHoraire({ demande, nomLogement, dureeMenageMin, onFermer, onDecide }) {
  const contratMin = enMin(demande.standard_time)
  const demandeMin = enMin(demande.requested_time)
  const [h, setH] = useState(demandeMin / 60)          // en HEURES décimales (le proto)
  const [volet, setVolet] = useState(null)             // null | 'refus'
  const [annonce, setAnnonce] = useState(null)
  const [arrivee, setArrivee] = useState(null)
  const [erreur, setErreur] = useState(null)
  const raisonRef = useRef(null)
  const timer = useRef(null)

  useEffect(() => {
    let vivant = true
    supabase.from('reservations')
      .select('id, start_date, guest_arrival_time, arrival_time, status')
      .eq('logement_id', demande.logement_id).eq('start_date', demande.event_date)
      .then(({ data, error }) => {
        if (!vivant) return
        const resa = !error && (data || []).find((r) => r.status !== 'cancelled')
        setArrivee({
          heure: resa ? (resa.guest_arrival_time || resa.arrival_time || null) : null,
          reelle: !!(resa && resa.guest_arrival_time),
        })
      })
    return () => { vivant = false }
  }, [demande])

  // Le montant se recalcule quand l'heure bouge — LES fonctions du cœur.
  useEffect(() => {
    clearTimeout(timer.current)
    timer.current = setTimeout(() => {
      const hhmm = `${String(Math.floor(h)).padStart(2, '0')}:${h % 1 ? '30' : '00'}`
      postAction({ action: 'services_hourly_annonce', booking_id: demande.id, time_hhmm: hhmm })
        .then((d) => setAnnonce({ ok: true, data: d }))
        .catch((e) => setAnnonce({ ok: false, motif: e.message }))
    }, 220)
    return () => clearTimeout(timer.current)
  }, [h, demande.id])

  const suivantMin = arrivee && arrivee.heure ? enMin(arrivee.heure) : null
  const duree = Number.isFinite(dureeMenageMin) && dureeMenageMin > 0 ? dureeMenageMin : null
  const hMinNum = Math.round(h * 60)
  const reste = suivantMin !== null ? suivantMin - hMinNum : null
  const tient = duree === null || reste === null || reste >= duree
  const a = annonce && annonce.ok ? annonce.data : null
  const tarifManquant = !!(a && a.tarif_manquant)
  const prenom = demande.voyageur || 'Le voyageur'
  const hFmtAcc = (v) => `${Math.floor(v)}h${v % 1 ? '30' : ''}`
  const borneH = Math.max(contratMin / 60,
    suivantMin !== null && duree !== null ? (suivantMin - duree) / 60 : demandeMin / 60)
  const chips = []
  for (let v = Math.ceil(contratMin / 60); v <= Math.floor(Math.min(borneH, contratMin / 60 + 6)); v += 1) {
    chips.push({ h: v, label: `${v}h` })
  }

  const accorder = useCallback(async () => {
    setErreur(null)
    try {
      await postAction({ action: 'services_orchestrateur_grant', booking_id: demande.id,
        granted_time: `${String(Math.floor(h)).padStart(2, '0')}:${h % 1 ? '30' : '00'}` })
      onFermer(); onDecide()
    } catch (e) { setErreur(e.message || 'Le serveur a refusé.') }
  }, [h, demande, onFermer, onDecide])

  const refuser = useCallback(async () => {
    setErreur(null)
    const raison = raisonRef.current ? raisonRef.current.innerText.trim() : ''
    try {
      await postAction({ action: 'services_orchestrateur_hourly_refuse', booking_id: demande.id,
        ...(raison ? { raison } : {}) })
      onFermer(); onDecide()
    } catch (e) { setErreur(e.message || 'Le serveur a refusé.') }
  }, [demande, onFermer, onDecide])

  const recapPhrase = annonce === null ? 'On calcule le montant…'
    : !annonce.ok ? (annonce.motif === 'services_hub_inactive'
      ? 'Le service des horaires est éteint.' : `Montant illisible : ${annonce.motif}`)
      : tarifManquant ? "Le tarif n'est pas renseigné sur ce logement — on ne facture pas un montant qu'on ne connaît pas."
        : (a.offert || a.montant_eur_cents === 0)
          ? 'Aucun frais — le départ tardif est offert sur ce logement.'
          : (<>Elle sera facturée de <b>{(a.montant_eur_cents / 100).toFixed(2).replace('.', ',')} €</b> pour ce départ tardif.</>)

  return (
    <Feuille testId="accueil-feuille-horaire"
      titre={volet === 'refus' ? 'Refuser la demande' : "Une demande d'horaire"}
      onFermer={onFermer}
      enfants={volet === 'refus'
        ? <FeuilleRefusHoraire nom={prenom} contratH={hFmtAcc(contratMin / 60)}
            raisonRef={raisonRef} onConfirmer={refuser} onRevenir={() => setVolet(null)}
            erreur={<Erreur texte={erreur} />} />
        : <FeuilleDemandeHoraire
            // LA FRISE (fichiers à jour 26/08 22h) : elle se recalcule à
            // chaque mouvement du curseur, avec le montant et le bouton.
            // Sans arrivée derrière, la barre se borne à la fin du curseur
            // et l'étiquette de droite dit l'heure seule — rien d'inventé.
            frise={{
              contratH: contratMin / 60,
              finH: suivantMin !== null ? suivantMin / 60
                : Math.max(borneH, demandeMin / 60),
              demandeH: demandeMin / 60,
              dureeH: duree ? duree / 60 : 0,
              h, ok: tient,
              voyageurLabel: prenom,
              hFmt: hFmtAcc,
              contratLabel: `${hFmtAcc(contratMin / 60)} · au contrat`,
              finLabel: suivantMin !== null
                ? `${hFmtAcc(suivantMin / 60)} · arrivée`
                : hFmtAcc(Math.max(borneH, demandeMin / 60)),
            }}
            sousTitre={`${nomLogement(demande.logement_id)} · ${prenom} demande à partir à ${heureCourte(demande.requested_time)}`}
            heroLb={suivantMin !== null
              ? `${prenom === 'Le voyageur' ? "S'il part" : `Si ${prenom} part`} à ${hFmtAcc(h)}, il reste pour le ménage`
              : `${prenom} demande à partir à ${hFmtAcc(demandeMin / 60)}`}
            heroVal={reste !== null
              ? (tient ? `${Math.floor(reste / 60)} h${reste % 60 ? ` ${reste % 60}` : ''}` : 'pas assez')
              : 'personne après'}
            heroKo={!tient}
            contratH={hFmtAcc(contratMin / 60)}
            arriveeH={suivantMin !== null ? hFmtAcc(suivantMin / 60) : '—'}
            arriveePleine={!!(arrivee && arrivee.reelle)}
            min={contratMin / 60} max={Math.max(borneH, demandeMin / 60)} valeur={h}
            onGlisse={setH} chips={chips}
            recapTitre={`${prenom} partira à ${hFmtAcc(h)}`}
            recapPhrase={recapPhrase}
            noteKo={!tient ? "Le ménage n'a plus le temps de tenir avant l'arrivée suivante." : null}
            accorderLb={`Accorder ${hFmtAcc(h)}`}
            accorderActif={tient && !!a && !tarifManquant}
            onAccorder={accorder} onRefuser={() => setVolet('refus')}
            erreur={<Erreur texte={erreur} />} />
      } />
  )
}

function enMin(hhmm) {
  const m = /^(\d{1,2}):(\d{2})/.exec(String(hhmm || ''))
  return m ? Number(m[1]) * 60 + Number(m[2]) : null
}

/* ═══ 3 · LE JUGEMENT COMPLET ══════════════════════════════════════ */
export function CtrlJugement({ incident, sousLoc, nomLogement, proprioNom, adminEmail,
  degForce = null, onFermer, onDecide }) {
  const restants = useMemo(() => articlesATrancher(incident), [incident])
  // `degForce` = « Revoir le jugement » depuis le suivi : on rouvre un article
  // DÉJÀ tranché, états préremplis depuis la dégradation.
  const deg = degForce || restants[0] || null
  const article = deg && (deg.type || 'Article')
  const [montant, setMontant] = useState(degForce && degForce.cout
    ? String(degForce.cout).replace('.', ',') : '')
  const [prixConnu, setPrixConnu] = useState(null)
  const initEtat = degForce ? degForce.etatFinancier : null
  const [jR, setJR] = useState(initEtat === ETAT.SANS_COUT
    || initEtat === ETAT.REMBOURSE_AIRCOVER ? 'proprio'
    : initEtat ? 'nous' : null)
  const [jRien, setJRien] = useState(initEtat === ETAT.SANS_COUT
    || (initEtat === ETAT.ACHAT_FBZ && !(degForce && degForce.aircoverDemande)))
  const [jAcOn, setJAcOn] = useState(!!(degForce && (degForce.aircoverDemande
    || initEtat === ETAT.REMBOURSE_AIRCOVER || initEtat === ETAT.AIRCOVER_REFACTURE)))
  const [acEtat, setAcEtat] = useState(degForce && parseFloat(degForce.aircoverMontant
    || degForce.aircoverFbz) > 0 ? 'rembourse'
    : degForce && degForce.aircoverRefuse ? 'refuse' : 'attente')
  const [acMontant, setAcMontant] = useState(degForce
    ? String(degForce.aircoverMontant || degForce.aircoverFbz || '').replace('.', ',') : '')
  const [jRef, setJRef] = useState(initEtat === ETAT.REFACTURE_PROPRIO
    || initEtat === ETAT.AIRCOVER_REFACTURE)
  const [erreur, setErreur] = useState(null)
  const [envoi, setEnvoi] = useState(false)

  useEffect(() => {
    if (!article) return undefined
    let vivant = true
    lirePrixArticle(article).then((p) => {
      if (!vivant || p === null) return
      setPrixConnu(p)
      setMontant((m) => (m === '' ? String(p).replace('.', ',') : m))
    })
    return () => { vivant = false }
  }, [article])

  // « À VÉRIFIER » (déclaré par un agent, jamais jugé) : l'admin JUGE ici —
  // récupérable (l'affaire s'arrête là) ou non (la suite s'ouvre). ADR-227 :
  // « orchestrateur OU admin ». Le choix s'écrit dans statutMenage par LE
  // même chemin (trancherQuiPaie).
  const aVerifier = !!(deg && deg.statutMenage === 'À vérifier')
  const [verdict, setVerdict] = useState(null) // 'recup' | 'nonrecup'

  const montantNum = parseFloat(String(montant).replace(',', '.'))
  const montantOk = Number.isFinite(montantNum) && montantNum > 0
  const refacForce = jR === 'nous' && jAcOn
  // « Comment c'est payé » : rien / AirCover / refacturé sont les voies —
  // la refacturation seule (nous + jRef) EST un jugement complet
  // (→ « Refacturé propriétaire »), pas un accessoire de « rien ».
  const pretJugement = !!jR
    && (jR === 'proprio' ? (jRien || jAcOn) : (jRien || jAcOn || jRef))
    && (jR === 'proprio' || montantOk)
    && (!jAcOn || acEtat !== 'rembourse' || parseFloat(String(acMontant).replace(',', '.')) > 0)
  // Récupérable = décision COMPLÈTE à elle seule ; non récupérable = le
  // jugement d'argent doit suivre ; pas encore jugé = rien ne part.
  const pret = aVerifier
    ? (verdict === 'recup' || (verdict === 'nonrecup' && pretJugement))
    : pretJugement

  // Le bilan — les phrases de jugBilan, recomposées sur l'état réel.
  const bilan = useMemo(() => {
    if (!jR) return []
    const l = []
    l.push(jR === 'proprio' ? "Le propriétaire rachète l'article lui-même."
      : `Nous avançons ${montantOk ? eurosCourt(montantNum) : '—'} pour le remplacement.`)
    if (jAcOn) {
      if (acEtat === 'attente') l.push("Une demande AirCover est ouverte, sans réponse à ce jour.")
      else if (acEtat === 'rembourse') l.push(`${eurosCourt(parseFloat(String(acMontant).replace(',', '.')) || 0)} remboursés par Airbnb.`)
      else l.push("Airbnb a refusé la demande — rien n'est couvert.")
    }
    if (jR === 'nous' && (jRef || refacForce) && !sousLoc) {
      l.push(`Nous lui refacturons ${montantOk ? eurosCourt(montantNum) : '—'} à son remplacement.`)
    }
    return l
  }, [jR, jAcOn, acEtat, acMontant, jRef, refacForce, sousLoc, montantOk, montantNum])

  const enregistrer = useCallback(async () => {
    if (!pret || envoi || !deg) return
    setEnvoi(true); setErreur(null)
    try {
      // Le verdict RÉCUPÉRABLE s'arrête là : l'article se lave ou se répare,
      // aucun argent n'entre en jeu.
      if (aVerifier && verdict === 'recup') {
        await trancherQuiPaie(incident.id, deg.id ?? '0', { statutMenage: 'Récupérable' })
        onFermer(); onDecide()
        return
      }
      const patch = {}
      if (aVerifier) patch.statutMenage = 'Non récupérable'
      const cout = montantOk ? String(montantNum) : null
      if (cout) patch.cout = cout
      const acM = parseFloat(String(acMontant).replace(',', '.'))
      if (jR === 'proprio') {
        patch.etatFinancier = jAcOn ? ETAT.REMBOURSE_AIRCOVER : ETAT.SANS_COUT
        if (jAcOn) {
          if (acEtat === 'attente') patch.aircoverDemande = true
          if (acEtat === 'rembourse') patch.aircoverMontant = String(acM)
          if (acEtat === 'refuse') { patch.aircoverRefuse = true }
        }
      } else {
        const refac = (jRef || refacForce) && !sousLoc
        patch.etatFinancier = jAcOn
          ? (refac ? ETAT.AIRCOVER_REFACTURE : ETAT.ACHAT_FBZ)
          : (refac ? ETAT.REFACTURE_PROPRIO : ETAT.ACHAT_FBZ)
        if (jAcOn) {
          if (acEtat === 'attente') patch.aircoverDemande = true
          if (acEtat === 'rembourse') {
            if (sousLoc) patch.aircoverFbz = String(acM)
            else patch.aircoverMontant = String(acM)
          }
          if (acEtat === 'refuse') patch.aircoverRefuse = true
        }
      }
      await trancherQuiPaie(incident.id, deg.id ?? '0', patch)
      if (montantOk && prixConnu === null) retenirPrixArticle(article, montantNum, adminEmail)
      onFermer(); onDecide()
    } catch (e) {
      setEnvoi(false); setErreur(e?.message || 'Le serveur a refusé.')
    }
  }, [pret, envoi, deg, aVerifier, verdict, jR, jAcOn, acEtat, acMontant, jRef,
    refacForce, sousLoc, montantOk, montantNum, prixConnu, article, adminEmail,
    incident, onFermer, onDecide])

  if (!deg) return null
  return (
    <Feuille testId="accueil-feuille-incident" titre="Un incident à trancher"
      suffixe={nomLogement(incident.logement_id)} onFermer={onFermer}
      enfants={
        <FeuilleJugement
          preliminaire={aVerifier ? { verdict, onVerdict: setVerdict } : null}
          article={article}
          sousJuge={['jugé non récupérable',
            incident.reported_by ? `par ${prenomDe(incident.reported_by)}` : null,
            incident.incident_date ? `le ${jourCourt(incident.incident_date)}` : null,
          ].filter(Boolean).join(' ')}
          montant={montant} onMontant={setMontant}
          habituel={prixConnu !== null
            ? `habituellement ${String(prixConnu).replace('.', ',')} €`
            : 'aucun prix connu — il servira les prochaines fois'}
          sousLoc={sousLoc}
          jR={jR} onJugR={(v) => { setJR(v); if (v === 'proprio') setJRef(false) }}
          jRien={jRien} onJRien={() => {
            setJRien(!jRien); if (!jRien) { setJAcOn(false); setJRef(false) } }}
          cautionRaison="pas encore branché — l'encaissement Stripe de la caution n'existe pas encore ici"
          jAcOn={jAcOn} onJAcOn={() => { setJAcOn(!jAcOn); if (!jAcOn) setJRien(false) }}
          acEtat={acEtat} onAcEtat={setAcEtat}
          acMontant={acMontant} onAcMontant={setAcMontant}
          jRef={jRef} onJRef={() => { setJRef(!jRef); if (!jRef) setJRien(false) }}
          refacForce={refacForce}
          refacSous={proprioNom
            ? `la ligne partira sur la facture de ${proprioNom}, au remplacement`
            : 'la ligne partira sur sa facture, au remplacement'}
          bilan={bilan} pret={pret && !envoi}
          enregistrerLb={envoi ? 'On enregistre…' : 'Enregistrer'}
          onEnregistrer={enregistrer} onAnnuler={onFermer}
          erreur={<Erreur texte={erreur} />} />
      } />
  )
}

/* ═══ 4 · LE SUIVI (ligne AirCover) ════════════════════════════════ */
export function CtrlSuivi({ incident, deg, sousLoc, nomLogement, echeance, todayIso,
  onFermer, onDecide, onRevoir }) {
  const [rachete, setRachete] = useState(deg.racheteLe || null)
  const [depose, setDepose] = useState(deg.remplaceLe || null)
  const [calOuvert, setCalOuvert] = useState(null)
  const [erreur, setErreur] = useState(null)
  const clos = !!(rachete && depose)
  const change = rachete !== (deg.racheteLe || null) || depose !== (deg.remplaceLe || null)

  const enregistrer = useCallback(async () => {
    setErreur(null)
    try {
      const patch = {}
      if (rachete !== (deg.racheteLe || null)) patch.racheteLe = rachete
      if (depose !== (deg.remplaceLe || null)) patch.remplaceLe = depose
      await trancherQuiPaie(incident.id, deg.id, patch)
      onFermer(); onDecide()
    } catch (e) { setErreur(e?.message || 'Le serveur a refusé.') }
  }, [rachete, depose, deg, incident, onFermer, onDecide])

  return (
    <Feuille testId="accueil-feuille-aircover"
      titre="Une réponse AirCover à saisir" suffixe={nomLogement(incident.logement_id)}
      onFermer={onFermer}
      enfants={
        <FeuilleSuivi
          echeance={echeance && echeance.phrase} echeanceTon={echeance && echeance.ton}
          recap={['Déclaré non récupérable',
            sousLoc ? 'racheté par nous' : null,
            deg.etatFinancier === 'AirCover + refacturé' ? 'refacturé au propriétaire' : null,
            `demande AirCover en attente${sousLoc ? ' — FBZ perçoit' : ''}`,
          ].filter(Boolean).join(' · ') + '.'}
          etatTitre={!rachete ? 'À racheter' : !depose ? 'À déposer dans le logement' : "C'est fait"}
          etatClos={clos}
          rachete={rachete ? jourCourt(rachete) : null}
          depose={depose ? jourCourt(depose) : null}
          onOuvrirRachete={() => setCalOuvert(calOuvert === 'rachete' ? null : 'rachete')}
          onOuvrirDepose={() => setCalOuvert(calOuvert === 'depose' ? null : 'depose')}
          calendrier={calOuvert ? (
            <MiniCalendrier todayIso={todayIso}
              valeur={calOuvert === 'rachete' ? rachete : depose}
              onPoser={(iso) => { (calOuvert === 'rachete' ? setRachete : setDepose)(iso); setCalOuvert(null) }}
              onVider={() => { (calOuvert === 'rachete' ? setRachete : setDepose)(null); setCalOuvert(null) }}
              onFermer={() => setCalOuvert(null)} />
          ) : null}
          etatPhrase={clos
            ? `Déposé le ${jourCourt(depose)} : l'incident est clos, et l'article revient au compte du linge.`
            : rachete ? `Racheté le ${jourCourt(rachete)}, pas encore déposé. Il reste sur votre accueil.`
              : "Pas encore racheté. Il reste sur votre accueil jusqu'à ce que vous le notiez."}
          enregistrerActif={change} onEnregistrer={enregistrer} onRevoir={onRevoir}
          erreur={<Erreur texte={erreur} />} />
      } />
  )
}

/* Le petit calendrier — jamais un sélecteur natif (repris du lot précédent,
   styles proto : cases 36 px, aujourd'hui navy plein). */
const MOIS_FR = ['janvier', 'février', 'mars', 'avril', 'mai', 'juin', 'juillet',
  'août', 'septembre', 'octobre', 'novembre', 'décembre']
export function MiniCalendrier({ valeur, onPoser, onVider, onFermer, todayIso }) {
  const [vy, vm] = (valeur || todayIso).split('-').map(Number)
  const [mois, setMois] = useState({ y: vy, m: vm - 1 })
  const prem = (new Date(Date.UTC(mois.y, mois.m, 1)).getUTCDay() + 6) % 7
  const nb = new Date(Date.UTC(mois.y, mois.m + 1, 0)).getUTCDate()
  const cases = []
  for (let i = 0; i < prem; i++) cases.push(null)
  for (let j = 1; j <= nb; j++) cases.push(j)
  const iso = (j) => `${mois.y}-${String(mois.m + 1).padStart(2, '0')}-${String(j).padStart(2, '0')}`
  return (
    <div data-testid="aircover-calendrier" style={{ background: '#fff',
      border: '1px solid #c3d5e4', borderRadius: '12px', padding: '11px', marginTop: '8px' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: '9px', marginBottom: '9px' }}>
        <button type="button" onClick={() => setMois((v) => (v.m ? { ...v, m: v.m - 1 } : { y: v.y - 1, m: 11 }))}
          style={{ width: '32px', height: '32px', borderRadius: '8px',
            border: '1px solid var(--bord)', background: '#fff', fontSize: '15px',
            color: 'var(--navy)', cursor: 'pointer' }}>‹</button>
        <span style={{ flex: 1, textAlign: 'center', fontSize: '12.5px',
          fontWeight: 600 }}>{MOIS_FR[mois.m]} {mois.y}</span>
        <button type="button" onClick={() => setMois((v) => (v.m === 11 ? { y: v.y + 1, m: 0 } : { ...v, m: v.m + 1 }))}
          style={{ width: '32px', height: '32px', borderRadius: '8px',
            border: '1px solid var(--bord)', background: '#fff', fontSize: '15px',
            color: 'var(--navy)', cursor: 'pointer' }}>›</button>
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7,1fr)', gap: '3px',
        marginBottom: '5px' }}>
        {['L', 'M', 'M', 'J', 'V', 'S', 'D'].map((x, i) => (
          <span key={i} style={{ textAlign: 'center', fontSize: '9.5px',
            color: 'var(--gris-d)' }}>{x}</span>))}
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7,1fr)', gap: '3px' }}>
        {cases.map((j, i) => (j === null ? <span key={i} />
          : <button key={i} type="button" onClick={() => onPoser(iso(j))}
              data-testid={iso(j) === todayIso ? 'aircover-cal-aujourdhui' : undefined}
              style={{ height: '36px', borderRadius: '9px', fontSize: '12px',
                cursor: 'pointer', fontFamily: 'inherit',
                ...(iso(j) === todayIso
                  ? { background: 'var(--navy)', color: '#fff', fontWeight: 600, border: 'none' }
                  : { background: '#fff', border: '1px solid var(--bord)',
                      color: 'var(--navy-d)' }) }}>{j}</button>))}
      </div>
      <div className="act" style={{ marginTop: '9px' }}>
        <button className="bt bt-s" onClick={onVider}>Effacer</button>
        <button className="bt bt-s" onClick={onFermer}>Fermer</button>
      </div>
    </div>
  )
}

/* ═══ 5 · UNE REPRISE EN ATTENTE ═══════════════════════════════════ */
export function CtrlReprise({ ligne, controle, run, onFermer, onDecide }) {
  const [choix, setChoix] = useState(null)
  const [detail, setDetail] = useState(null)
  const [raisonTick, setRaisonTick] = useState(0)
  const [erreur, setErreur] = useState(null)
  const [envoi, setEnvoi] = useState(false)
  const raisonRef = useRef(null)

  useEffect(() => {
    let vivant = true
    if (controle && controle.id) {
      fetchControleDetail(controle.id)
        .then((r) => { if (vivant && r) setDetail(r) })
        .catch((e) => console.error('[accueil] controle_detail', e?.message || e))
    }
    return () => { vivant = false }
  }, [controle])

  const raisonTexte = () => (raisonRef.current ? raisonRef.current.innerText.trim() : '')
  const pret = choix === 'moi' || choix === 'agent'
    || (choix === 'ok' && raisonTexte().length >= 3)

  const valider = useCallback(async () => {
    if (envoi) return
    setEnvoi(true); setErreur(null)
    const corps = choix === 'moi' ? { choix: 'moi' }
      : choix === 'agent' ? { choix: 'agent', agentEmail: run && run.assigned_provider_email }
        : { choix: 'bon', raison: raisonTexte() }
    const { ok, erreur: err } = await deciderReprise({ controleId: controle.id, ...corps })
    setEnvoi(false)
    if (!ok) { setErreur(err || 'Le serveur a refusé.'); return }
    onFermer(); onDecide(ligne.cle)
  }, [choix, envoi, run, controle, ligne, onFermer, onDecide])

  const points = ((detail && detail.points) || []).map((pt) => ({
    titre: pt.texte || pt.categorie || 'Point',
    sous: pt.categorie && pt.texte ? pt.categorie : '',
    photo: Array.isArray(pt.photos) && pt.photos.length ? pt.photos[0] : null,
  }))

  return (
    <Feuille testId="accueil-feuille-menage" titre="Une reprise en attente" onFermer={onFermer}
      enfants={
        <div data-testid="feuille-reprise-decision" onInput={() => setRaisonTick(raisonTick + 1)}>
          <FeuilleReprise
            sousTitre={ligne.sousTitre
              ? `${ligne.titre} · ${controle && controle.cree_le ? `demandée depuis le ${jourCourt(String(controle.cree_le).slice(0, 10))}` : ligne.sousTitre.split(' · ').slice(1).join(' · ')}`
              : ligne.titre}
            points={points.length ? points : [{ titre: 'Détail indisponible', sous: '' }]}
            anonymat={controle && controle.controleur_email
              ? <>Contrôlé par {prenomDe(controle.controleur_email)}. <b>L'agent ne saura jamais qui.</b></>
              : null}
            choix={choix} onChoix={(c) => setChoix(choix === c ? null : c)}
            raisonRef={raisonRef} pret={pret && !envoi}
            onValider={valider} onAnnuler={onFermer}
            erreur={<Erreur texte={erreur} />} />
        </div>
      } />
  )
}

/* ═══ 6 · UN CONTRÔLE SANS PERSONNE ════════════════════════════════ */
export function CtrlSansPersonne({ ligne, run, optionsAgents, onFermer, onDecide, onMoiMeme }) {
  const [choisi, setChoisi] = useState(null)
  const [erreur, setErreur] = useState(null)
  const [envoi, setEnvoi] = useState(false)
  const assigne = run && (run.assigned_provider_email || '').trim()
  const roster = rosterDepuisOptions(optionsAgents.filter((o) => o.value !== assigne))

  const confier = useCallback(async () => {
    if (!choisi || envoi) return
    setEnvoi(true); setErreur(null)
    const { ok, erreur: err } = await designerControleurRun({ runId: ligne.runId, controleurEmail: choisi })
    setEnvoi(false)
    if (!ok) { setErreur(err || 'Le serveur a refusé.'); return }
    onFermer(); onDecide(ligne.cle)
  }, [choisi, envoi, ligne, onFermer, onDecide])

  const heureDeClot = (iso) => {
    const d = iso ? new Date(iso) : null
    if (!d || Number.isNaN(d.getTime())) return null
    const [hh, mm] = new Intl.DateTimeFormat('fr-FR', { hour: '2-digit', minute: '2-digit',
      hour12: false, timeZone: 'Europe/Paris' }).format(d).split(':')
    return mm === '00' ? `${Number(hh)}h` : `${Number(hh)}h${mm}`
  }

  return (
    <Feuille testId="accueil-feuille-menage" titre="Un contrôle sans personne" onFermer={onFermer}
      enfants={
        <div data-testid="feuille-confier-controle">
          <FeuilleControleSansPersonne
            sousTitre={ligne.sousTitre || ligne.titre}
            faitPar={[assigne ? `Fait par ${prenomDe(assigne)}` : 'Fait — agent inconnu',
              run && run.completed_at ? `clos à ${heureDeClot(run.completed_at)}` : null,
              `${((run && run.photos) || []).length} photo${((run && run.photos) || []).length > 1 ? 's' : ''}`,
            ].filter(Boolean).join(' · ')}
            agents={roster} choisi={choisi}
            onChoisir={(id) => setChoisi(choisi === id ? null : id)}
            exclusion={assigne ? `${prenomDe(assigne)} a fait ce ménage — il ne peut pas le contrôler.` : null}
            onConfier={confier} onAnnuler={onFermer} onMoiMeme={onMoiMeme}
            erreur={<Erreur texte={erreur} />} />
        </div>
      } />
  )
}

/* ═══ LA CONVERSATION (carte Messages) ═════════════════════════════
   La feuille du fichier livré. ⚠️ Le bloc « L'opérateur propose » ne se rend
   que si un brouillon RÉEL existe — le copilote n'expose pas encore de
   brouillon par fil côté accueil, donc `operateur` reste null en prod ; le
   branchement (Envoyer le brouillon / Écarter / relancer au micro) est prêt
   et s'allumera avec lui. Rien n'est inventé à sa place. */
export function CtrlFil({ fil, messages, nomLogement, brouillon = null,
  propositionPrete = null, onFermer, onEnvoye, onMicro }) {
  const [envoiEtat, setEnvoiEtat] = useState(null)
  const [erreur, setErreur] = useState(null)
  const [ecarte, setEcarte] = useState(false)
  const [resa, setResa] = useState(null)
  const reponseRef = useRef(null)

  const duFil = useMemo(() => (messages || [])
    .filter((m) => (m.thread_id || m.id) === fil.threadId)
    .sort((a, b) => String(a.created_at).localeCompare(String(b.created_at))), [messages, fil])
  const dernierEntrant = [...duFil].reverse().find((m) => m.direction === 'inbound')

  // ⚠️ LA RÉSERVATION DU FIL SE RÉSOUT ICI (correction 27/08 — le faux
  // succès OTA) : sans elle, beds24BookingId partait nul et le serveur
  // répondait 200 « skipped_no_coordinate » — la feuille se fermait sur du
  // vide. inbox_messages porte reservation_id : on la lit.
  useEffect(() => {
    let vivant = true
    const resaId = duFil.map((m) => m.reservation_id).find(Boolean)
    if (!resaId) { setResa(null); return undefined }
    supabase.from('reservations')
      .select('id, logement_id, source, start_date, end_date, beds24_booking_id, guest_email')
      .eq('id', resaId).limit(1)
      .then(({ data, error }) => {
        if (vivant && !error && data && data[0]) setResa(data[0])
      })
    return () => { vivant = false }
  }, [duFil])

  const envoyerTexte = useCallback(async (corps) => {
    if (!corps || envoiEtat === 'envoi') return
    setEnvoiEtat('envoi'); setErreur(null)
    try {
      const { data: { session } } = await supabase.auth.getSession()
      const res = await fetch('/api/send-email', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json',
          Authorization: `Bearer ${session?.access_token}` },
        body: JSON.stringify({ type: 'inbox_reply', data: {
          threadId: fil.threadId,
          canaux: [dernierEntrant && dernierEntrant.canal ? dernierEntrant.canal : 'email'],
          body: corps,
          subject: dernierEntrant && dernierEntrant.subject
            ? (dernierEntrant.subject.startsWith('Re:') ? dernierEntrant.subject : `Re: ${dernierEntrant.subject}`)
            : null,
          reservationId: resa ? resa.id : null,
          logementId: (dernierEntrant && dernierEntrant.logement_id) || (resa && resa.logement_id) || null,
          toEmail: (dernierEntrant && dernierEntrant.sender_email) || (resa && resa.guest_email) || null,
          beds24BookingId: resa ? resa.beds24_booking_id : null,
          inReplyToMessageId: dernierEntrant ? dernierEntrant.id : null,
          iaDraft: brouillon && corps === brouillon.texte ? brouillon.texte : null,
        } }),
      })
      const json = await res.json().catch(() => ({}))
      if (!res.ok) throw new Error(json.error || `Erreur ${res.status}`)
      // ⚠️ LIRE LES RESULTS, PAS LA CONCLUSION (correction 27/08) : le
      // serveur répond 200 même quand chaque canal a été SAUTÉ. Un skipped
      // n'est pas un envoi — la feuille NE se ferme PAS, elle dit pourquoi.
      const resultats = Array.isArray(json.results) ? json.results : []
      const passes = resultats.filter((r) => r
        && !String(r.status || '').startsWith('skipped'))
      if (resultats.length > 0 && passes.length === 0) {
        const raisons = resultats.map((r) => r.reason || r.status).join(' · ')
        throw new Error(`Rien n'est parti — ${raisons}`)
      }
      // ⚠️ UN ÉCHEC N'EST PAS UN ENVOI (correction 29/09) : `failed` passait le
      // filtre des `skipped*`, la feuille se fermait comme si c'était parti.
      // Elle reste ouverte, le texte gardé : un clic le renvoie.
      const echecs = resultats.filter((r) => r && r.status === 'failed')
      if (echecs.length > 0) {
        const raisons = echecs.map((r) => r.reason || r.error || 'échec').join(' · ')
        throw new Error(`Non parti — ${raisons}. Le texte est gardé : renvoyez-le.`)
      }
      // Et une SIMULATION n'est pas un envoi non plus : en mode essai, rien
      // n'est parti chez le voyageur — la feuille le dit au lieu de se fermer.
      if (resultats.length > 0 && resultats.every((r) => r && r.status === 'sent_dry_run')) {
        throw new Error("Simulé (mode essai) : rien n'est parti au voyageur.")
      }
      onFermer(); onEnvoye && onEnvoye()
    } catch (e) {
      setEnvoiEtat(null); setErreur(e?.message || "L'envoi a échoué.")
    }
  }, [envoiEtat, fil, dernierEntrant, resa, brouillon, onFermer, onEnvoye])

  const ini = (nom) => String(nom || '?').split(/\s+/).map((x) => x[0]).join('').slice(0, 2).toUpperCase()
  const canalTag = dernierEntrant && dernierEntrant.canal === 'airbnb' ? 'le chat Airbnb'
    : dernierEntrant && dernierEntrant.canal === 'booking' ? 'le chat Booking'
      : dernierEntrant && dernierEntrant.canal === 'direct' ? 'le chat Direct' : "l'email"

  return (
    <Feuille testId="accueil-feuille-fil" titre={fil.titre}
      suffixe={nomLogement && fil.logementId ? nomLogement(fil.logementId) : null}
      onFermer={onFermer}
      enfants={<>
        {/* LOT 1 OPÉRATEUR (D1) — MODE RENVOI : quand une réponse est PRÊTE
            dans la file, le fil le DIT et EMMÈNE — il ne décide pas. Les
            boutons Envoyer/Écarter du bloc opérateur (FeuillesComplement) ne
            s'allument JAMAIS : la surface d'envoi reste unique (l'écran des
            décisions). Le brouillon ne se lit pas ici (il peut porter un code
            d'accès — ADR-303). */}
        {propositionPrete && (
          <div data-testid="fil-proposition-prete"
            style={{ display: 'flex', alignItems: 'center', gap: 10,
              padding: '10px 12px', marginBottom: 12,
              background: 'var(--ciel-p)', border: '1px dashed #87b8e6',
              borderRadius: 12 }}>
            <span className="tag t-b">prête</span>
            <span style={{ flex: 1, minWidth: 0, fontSize: 11.5,
              color: 'var(--navy-d)', lineHeight: 1.5 }}>
              L'opérateur a préparé une réponse. Elle vous attend dans vos
              décisions.
            </span>
            <button type="button" data-testid="fil-proposition-voir"
              onClick={propositionPrete.onVoir}
              style={{ minHeight: 38, padding: '0 13px', borderRadius: 9,
                background: 'var(--navy)', color: '#fff', fontSize: 11.5,
                fontWeight: 600, border: 'none', cursor: 'pointer',
                flexShrink: 0 }}>
              Y aller
            </button>
          </div>
        )}
        <FeuilleFilMessage
          resaLigne={resa ? `${resa.start_date} → ${resa.end_date}` : null}
          onOuvrirResa={resa && fil.onOuvrirResa ? fil.onOuvrirResa : null}
          bulles={duFil.map((m) => ({
            ini: m.direction === 'inbound' ? ini(m.sender_name) : 'VS',
            deMoi: m.direction !== 'inbound',
            // Une réponse SIMULÉE (mode essai) n'est pas partie : elle ne se
            // dessine pas comme un envoi sans le dire.
            texte: m.direction !== 'inbound' && m.metadata && m.metadata.dry_run === true
              ? `Simulé, non envoyé au voyageur — ${m.body || ''}`
              : m.body,
            quand: m.created_at ? String(m.created_at).slice(11, 16) : '',
          }))}
          operateur={brouillon && !ecarte ? {
            resume: brouillon.resume || null,
            texte: brouillon.texte,
            onEnvoyer: () => envoyerTexte(brouillon.texte),
            onEcarter: () => setEcarte(true),
          } : null}
          canal={canalTag}
          reponseRef={reponseRef}
          onEnvoyer={() => envoyerTexte(reponseRef.current ? reponseRef.current.innerText.trim() : '')}
          onMicro={onMicro}
          envoiEtat={envoiEtat}
          erreur={<Erreur texte={erreur} />} />
      </>} />
  )
}

/* ═══ MES TÂCHES — la feuille du fichier livré ═════════════════════
   « ＋ Nouvelle tâche » RENVOIE vers Aujourd'hui · Mes tâches — la création
   vit là-bas, l'accueil ne refait pas cet écran. */
export function CtrlTaches({ taches, todayIso, onFermer, onAller }) {
  const ouvertes = (taches || []).filter((t) => t && t.status !== 'done' && !t.archived_at)
  const duJour = (t) => t.due_at && String(t.due_at).slice(0, 10)
  const retard = ouvertes.filter((t) => duJour(t) && duJour(t) < todayIso)
  const aujourdhui = ouvertes.filter((t) => duJour(t) === todayIso)
  return (
    <Feuille testId="accueil-feuille-taches" titre="Mes tâches" onFermer={onFermer}
      enfants={
        <FeuilleTaches
          retard={retard.map((t) => ({ titre: t.title, sous: jourCourt(duJour(t)) }))}
          aujourdhui={aujourdhui.map((t) => ({ titre: t.title, sous: jourCourt(duJour(t)) }))}
          onNouvelle={() => { onFermer(); onAller('mes-taches') }} />
      } />
  )
}

/* ═══ PROSPECTION — la feuille du fichier livré ════════════════════
   Les trois mieux notés RÉELS (score + contact), chaque ligne ouvre
   L'ANNONCE D'ORIGINE (le lien du prospect). */
export function CtrlProspects({ compte, onFermer }) {
  const [lignes, setLignes] = useState(null)
  const [erreur, setErreur] = useState(null)

  useEffect(() => {
    let vivant = true
    ;(async () => {
      try {
        const { data: { session } } = await supabase.auth.getSession()
        const res = await fetch('/api/prospects', {
          headers: session?.access_token
            ? { Authorization: `Bearer ${session.access_token}` } : {},
        })
        const json = await res.json().catch(() => ([]))
        if (!res.ok) throw new Error(json?.error || `Erreur ${res.status}`)
        const tous = Array.isArray(json) ? json : (json.prospects || [])
        const top = tous
          .filter((x) => x && (x.email || x.tel) && x.lien && Number(x.score) >= 1)
          .sort((a, b) => Number(b.score) - Number(a.score))
          .slice(0, 3)
          .map((x) => ({
            score: String(x.score),
            titre: [x.type_logement || x.type, x.adresse || x.quartier]
              .filter(Boolean).join(', ') || x.nom || 'Annonce',
            sous: [x.surface ? `${x.surface} m²` : null,
              x.prix ? `${x.prix} €` : null, x.site || x.source]
              .filter(Boolean).join(' · '),
            onOuvrir: () => window.open(x.lien, '_blank', 'noopener'),
          }))
        if (vivant) setLignes(top)
      } catch (e) {
        console.error('[accueil] prospects', e?.message || e)
        if (vivant) { setLignes([]); setErreur('La lecture des annonces a échoué.') }
      }
    })()
    return () => { vivant = false }
  }, [])

  return (
    <Feuille testId="accueil-feuille-prospects" titre="Prospection" onFermer={onFermer}
      enfants={
        <FeuilleProspects
          info={<>{(compte && compte.total) || 0} annonces collectées.{' '}
            <b>{(compte && compte.aRegarder) || 0} piste{((compte && compte.aRegarder) || 0) > 1 ? 's' : ''} à regarder</b>
            {' '}— à travailler dans Logements › À démarcher.</>}
          lignes={lignes === null ? [] : lignes}
          note={lignes === null ? 'On lit les annonces…'
            : "Chaque ligne ouvre l'annonce d'origine. Le score est établi sur votre cible : meublé à Toulouse, 15 à 80 m², annonce de particulier."}
          erreur={<Erreur texte={erreur} />} />
      } />
  )
}

/* ═══ LE DÉTAIL D'UN MÉNAGE — la feuille du fichier livré ══════════
   L'agent et son état, les deux heures (plein = formulaire, pointillés =
   contrat), les trois renvois (codes / contrôler / incidents), la checklist
   réelle et ses photos signées. */
export function CtrlMenageDetail({ ligne, run, controle, incidents, creneau,
  nomLogement, todayIso, onFermer, onAller, onControler, onPhoto }) {
  const [pieces, setPieces] = useState(null)
  const [photos, setPhotos] = useState([])

  useEffect(() => {
    let vivant = true
    if (run && run.logement_id) {
      fetchLogInfo(run.logement_id, run.scheduled_date)
        .then((info) => { if (vivant) setPieces((info && info.menage && info.menage.pieces) || []) })
        .catch((e) => { console.error('[accueil] log info', e?.message || e); if (vivant) setPieces([]) })
    } else setPieces([])
    ;(async () => {
      try {
        if (!run) return
        const { data: { session } } = await supabase.auth.getSession()
        const res = await fetch(`/api/admin-users?action=menage_run_photos&run_id=${encodeURIComponent(run.id)}`,
          { headers: session?.access_token ? { Authorization: `Bearer ${session.access_token}` } : {} })
        const json = await res.json().catch(() => ({}))
        if (res.ok && vivant) setPhotos(Array.isArray(json?.photos) ? json.photos : [])
      } catch (e) { console.error('[accueil] photos run', e?.message || e) }
    })()
    return () => { vivant = false }
  }, [run])

  if (!run) return null
  const prenom = prenomDe(run.assigned_provider_email)
  const heureLoc = (iso) => {
    const d = iso ? new Date(iso) : null
    if (!d || Number.isNaN(d.getTime())) return null
    const [hh, mm] = new Intl.DateTimeFormat('fr-FR', { hour: '2-digit', minute: '2-digit',
      hour12: false, timeZone: 'Europe/Paris' }).format(d).split(':')
    return mm === '00' ? `${Number(hh)}h` : `${Number(hh)}h${mm}`
  }
  const etat = run.status === 'completed'
    ? `validé à ${heureLoc(run.completed_at) || '—'}`
    : run.status === 'in_progress' ? 'en cours' : 'à venir'
  const checked = run.items_checked && typeof run.items_checked === 'object' ? run.items_checked : {}
  const nbCoches = Object.values(checked).reduce((n, l) => n + (Array.isArray(l) ? l.length : 0), 0)
  const nbTotal = (pieces || []).reduce((n, pc) => n + ((pc.items || []).length), 0)
  const parPiece = groupPhotosByPiece(photos.length ? photos : (run.photos || []))
  const incidentsDuRun = (incidents || []).filter((i) => i && i.run_id === run.id)
  const aJuger = incidentsDuRun.filter((i) => i.status === 'en_cours').length

  const listePieces = (pieces || []).map((pc) => {
    const phs = (parPiece[pc.id] || []).map((ph) => ph.signed_url || ph.url).filter(Boolean)
    const cochees = Array.isArray(checked[pc.id]) ? checked[pc.id].length : 0
    const total = (pc.items || []).length
    return {
      nom: pc.name || pc.id,
      lignes: `${total} ligne${total > 1 ? 's' : ''}`,
      tip: run.status === 'completed' ? 'valide' : 'pas_valide',
      photos: (pc.photos_min || 0) === 0 && phs.length === 0 ? null : phs,
      cochees,
    }
  })
  // Les photos hors config (pièce disparue) restent visibles — jamais muettes.
  for (const cle of Object.keys(parPiece)) {
    if (!(pieces || []).some((pc) => pc.id === cle)) {
      listePieces.push({ nom: cle === '__sans_piece__' ? 'Autres photos' : cle,
        lignes: '', tip: null,
        photos: parPiece[cle].map((ph) => ph.signed_url || ph.url).filter(Boolean) })
    }
  }

  return (
    <Feuille testId="accueil-feuille-menage-detail"
      titre={nomLogement(run.logement_id)} onFermer={onFermer}
      enfants={
        <FeuilleMenageDetail
          agent={{
            ini: prenom ? prenom.slice(0, 2).toUpperCase() : '—',
            nom: prenom || 'Personne',
            sous: prenom ? etat : 'personne sur ce ménage',
            tip: run.status === 'completed' ? 'valide' : 'pas_valide',
          }}
          heures={{
            departH: (creneau && creneau.departH) || '—',
            departSure: !!(creneau && creneau.departSur),
            arriveeH: (creneau && creneau.arriveeH) || '—',
            arriveeSure: !!(creneau && creneau.arriveeSur),
          }}
          boutons={{
            codesSous: 'sur l\'écran ménage',
            onCodes: () => { onFermer(); onAller('menage-today') },
            controlerSous: controle ? 'contrôle passé'
              : (run.controleur_designe_email
                ? `${prenomDe(run.controleur_designe_email)} est désigné` : 'personne dessus'),
            controlerTip: controle
              ? (controle.statut === 'a_reprendre' || controle.statut === 'reprise_en_cours'
                ? 'ctrl_reprendre' : 'ctrl_ras')
              : (run.controleur_designe_email ? 'ctrl_designe' : 'ctrl_personne'),
            onControler: () => { onFermer(); onControler(run.id) },
            incidentsSous: aJuger ? `${aJuger} à juger`
              : incidentsDuRun.length ? `${incidentsDuRun.length} réglé${incidentsDuRun.length > 1 ? 's' : ''}` : 'aucun',
            incidentsTip: aJuger ? 'incident_a_juger'
              : incidentsDuRun.length ? 'incident_regle' : null,
            onIncidents: () => { onFermer(); onAller('incidents') },
          }}
          checklist={{
            titre: pieces === null ? 'On lit la checklist…'
              : nbTotal ? `${nbCoches} sur ${nbTotal}${nbCoches >= nbTotal ? ' · tout est coché' : ''}`
                : 'Aucune checklist configurée',
            photosLabel: `${photos.length || (run.photos || []).length} photo${(photos.length || (run.photos || []).length) > 1 ? 's' : ''}`,
            pieces: listePieces,
            onPhoto: (url) => onPhoto && onPhoto(url),
          }} />
      } />
  )
}

/* ═══ LE MICRO DE L'ACCUEIL ════════════════════════════════════════
   Le bouton du proto (flottant, 62 px, #87b8e6) et son panneau en bascule —
   TOUT SE PASSE SUR PLACE, on ne quitte jamais la page. ⚠️ Le CONTENU du
   panneau (l'opérateur qui écoute, comprend, propose) est HORS LOT : il
   arrivera avec le chantier opérateur. Le panneau dit l'état actuel — règle
   « textes d'aide = état réel », jamais une promesse muette.
   NOTE : sur téléphone, le proto le loge dans la BARRE HAUTE de l'app — elle
   est partagée (hors de cet écran) ; en attendant l'arbitrage, le bouton
   reste flottant aux deux tailles. */
const microIc = (t) => (
  <svg width={t} height={t} viewBox="0 0 24 24" fill="none" stroke="currentColor"
    strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <rect x="9" y="2" width="6" height="11" rx="3" />
    <path d="M5 10v1a7 7 0 0 0 14 0v-1" /><path d="M12 18v4" />
  </svg>
)
// MicroAccueil — SUPPRIMÉ le 2026-09-07 (décision Dimitri). Il doublonnait
// avec `BoutonMicro` (src/views/accueil/BoutonMicro.jsx), monté par App.jsx sur
// toutes les pages admin : même position fixe (right:20 bottom:20), z-index 35
// contre 70 — le flottant global le recouvrait sur l'Accueil. Depuis le panneau
// de la voix, celui-ci mentait en plus (« pas encore branché » alors que
// l'autre écoute vraiment).
// « Un bouton qui dit "pas encore branché" alors qu'un autre marche juste
//   au-dessus, c'est le pire des deux mondes. » — Dimitri.
// Le testid `accueil-micro` disparaît avec lui ; `accueil-bouton-micro` (le
// flottant conservé) reste la seule adresse du micro.

/* « JE CONTRÔLE MAINTENANT » — l'écran PARTAGÉ du contrôleur, monté tel quel,
   ouvert sur CE ménage. Aucune règle recopiée. */
export function CtrlControleEmbarque({ runId, cfg, currentUser, onFermer }) {
  return (
    <PortailFeuille>
      <div role="dialog" aria-modal="true" aria-label="Contrôle du ménage"
        data-testid="accueil-feuille-controle-embarque"
        style={{ position: 'fixed', inset: 0, zIndex: 9000, background: '#f8fafc',
          overflowY: 'auto' }}>
        <div style={{ maxWidth: 640, margin: '0 auto', padding: '10px 12px 30px' }}>
          <button type="button" onClick={onFermer} className="fbz-press fbz-tap"
            data-testid="controle-embarque-fermer"
            style={{ display: 'inline-flex', alignItems: 'center', gap: 6, minHeight: 44,
              background: 'none', border: 'none', color: '#5c6773', fontSize: 12.5,
              fontWeight: 700, cursor: 'pointer', padding: '4px 6px',
              fontFamily: 'inherit' }}>
            ✕ Fermer — retour à l'accueil
          </button>
          <ControleView cfg={cfg} currentUser={currentUser}
            destination={{ runId }} onDestinationConsommee={() => {}} />
        </div>
      </div>
    </PortailFeuille>
  )
}

/* ═══ « UN DÉPART A BOUGÉ » (arbitrage 27/08) ══════════════════════
   Le signal de la cascade (depart_reporte_au) : le départ de la résa a
   changé SANS que le ménage puisse suivre (en cours, fait, ou date passée).
   La feuille dit le fait, et « C'est traité » efface le signal — la ligne
   sort, comme toute décision de l'écran. */
export function CtrlDepartReporte({ ligne, run, nomLogement, onFermer, onDecide }) {
  const [erreur, setErreur] = useState(null)
  const [envoi, setEnvoi] = useState(false)

  const traiter = useCallback(async () => {
    if (envoi || !run) return
    setEnvoi(true); setErreur(null)
    const { data, error } = await supabase.from('menage_runs')
      .update({ depart_reporte_au: null }).eq('id', run.id).select('id')
    setEnvoi(false)
    if (error || !data || data.length === 0) {
      setErreur('Le signal n\'a pas pu être effacé.'); return
    }
    onFermer(); onDecide(ligne.cle)
  }, [envoi, run, ligne, onFermer, onDecide])

  if (!run) return null
  const etatRun = run.status === 'completed' ? 'déjà fait'
    : run.status === 'in_progress' ? 'en cours'
      : run.status === 'skipped' ? 'retiré (la date était passée)' : run.status
  return (
    <Feuille testId="accueil-feuille-depart-reporte" titre="Un départ a bougé"
      suffixe={nomLogement(run.logement_id)} onFermer={onFermer}
      enfants={
        <>
          <div className="info">Le départ du voyageur est passé
            au <b>{jourCourt(run.depart_reporte_au)}</b> — le ménage
            du {jourCourt(run.scheduled_date)} était <b>{etatRun}</b>, il n'a
            pas suivi tout seul.</div>
          <p className="note">À vous de voir : replanifier un passage, poser une
            intervention, ou rien si c'est déjà couvert. « C'est traité »
            efface le signal — il reviendra si le départ bouge encore.</p>
          <div className="act">
            <button className="bt bt-p" data-testid="depart-reporte-traite"
              onClick={traiter} disabled={envoi}>
              {envoi ? 'On efface…' : "C'est traité"}</button>
            <button className="bt bt-s" onClick={onFermer}>Annuler</button>
          </div>
          <Erreur texte={erreur} />
        </>
      } />
  )
}

/* ═══ CE QU'UN VOYAGEUR NOUS A DIT ═════════════════════════════════
   Une seule feuille pour les DEUX familles (demande Dimitri 2026-09-05 :
   « traite les deux ensemble ») :
     · le signalement du séjour   — « un problème » / « une casse » ;
     · l'état des lieux d'arrivée — le constat des 24 premières heures.

   ⚠️ LE TEXTE ET LES PHOTOS PARTENT AU CLIC, jamais avant : la liste de
   l'accueil ne transporte pas ce que le voyageur a écrit (règle « aucune
   feuille de détail n'est pré-chargée », et bonne hygiène pour un texte
   libre qui peut contenir un nom ou un numéro).

   ⚠️ ZÉRO ARGENT SUR CETTE FEUILLE, ET C'EST LA RÈGLE : pas de montant, pas
   de retenue sur caution, pas de lien vers un prélèvement. Un signalement
   INFORME. La qualification et l'argent vivent dans le chantier des
   incidents, avec ses propres gardes — cette feuille ne s'en approche pas.

   ⚠️ « SUPPRIMER DÉFINITIVEMENT » EXISTE POUR UNE RAISON PRÉCISE (RGPD) : le
   texte est libre, un voyageur y écrira un jour son nom, son téléphone ou
   celui d'un tiers. La ligne est RÉELLEMENT supprimée, photos comprises ; la
   trace serveur garde QUI et QUAND, jamais le CONTENU.

   [D25] LES TROIS SUITES (maquette voyageur v4, décision 25) — « C'est vu »
   est remplacé par les trois gestes que la maquette nomme :
     · OUVRIR UN INCIDENT — crée un incident dans le chantier EXISTANT
       (createIncident, le chemin d'IncidentsTab : source admin_manual,
       statut initial en_cours). ⚠️ SANS MONTANT : aucun champ de coût n'est
       posé ni posable ici — la qualification et l'argent vivent dans le
       chantier des incidents, avec ses gardes.
     · RÉPONDRE — ouvre le fil du voyageur, là où la bulle du signalement
       est déjà arrivée. Le marquage « répondu » ne se pose qu'APRÈS un
       envoi réussi (câblé dans AccueilAdmin, sur onEnvoye du fil).
     · CLASSER SANS SUITE — la ligne sort du bandeau et RESTE en base :
       un signalement classé ne disparaît jamais de l'historique du séjour
       (sa bulle reste aussi lisible dans la conversation du voyageur). */
export function CtrlSignalementVoyageur({ famille, id, titre, sousTitre,
  adminEmail, onFermer, onDecide, onRepondre, onPhoto }) {
  const [detail, setDetail] = useState(null)
  const [etat, setEtat] = useState('lecture')  // lecture | pret | absent | echec
  const [erreur, setErreur] = useState(null)
  const [envoi, setEnvoi] = useState(false)
  const [confirmeSuppr, setConfirmeSuppr] = useState(false)

  useEffect(() => {
    let annule = false
    const lire = async () => {
      try {
        const json = await postAction({
          action: 'signalement_voyageur_detail', famille, id,
        })
        if (annule) return
        setDetail(json && json.signalement ? json.signalement : null)
        setEtat(json && json.signalement ? 'pret' : 'absent')
      } catch (e) {
        if (annule) return
        // ⚠️ « la lecture a échoué » et « le module n'est pas actif » sont
        // DEUX phrases différentes — jamais un vide muet.
        const code = String((e && e.message) || '')
        if (code === 'service_unavailable') {
          setEtat('absent')
          setErreur("Ce module n'est pas encore actif en base.")
        } else {
          setEtat('echec')
          setErreur('La lecture de ce signalement a échoué.')
        }
      }
    }
    lire()
    return () => { annule = true }
  }, [famille, id])

  // La phrase d'un refus de suite — les trois gestes partagent les mêmes.
  const phraseRefusSuite = (e, sinon) => {
    const code = String((e && e.message) || '')
    if (code === 'deja_traite') {
      return "Quelqu'un vient de traiter ce signalement — l'écran se met à jour."
    }
    if (code === 'issue_migration_absente') {
      return 'Ce geste attend sa mise en base (migration des suites préparée, pas encore appliquée).'
    }
    return sinon
  }

  /* [D25] SUITE 1 — OUVRIR UN INCIDENT. Le chemin EXISTANT du chantier des
     incidents (createIncident, celui d'IncidentsTab : INSERT client sous RLS
     admin_all, source admin_manual, statut initial en_cours). ⚠️ SANS UN MOT
     D'ARGENT : la dégradation part « À vérifier », coût vide, état financier
     à qualifier — exactement le défaut de l'UI existante. Le texte du
     voyageur est recopié dans la description de l'incident : c'est ce que
     l'admin qualifiera là-bas (en base, jamais dans un journal). */
  const ouvrirIncident = useCallback(async () => {
    if (envoi || !detail) return
    if (!detail.logement_id) {
      setErreur("Ce signalement n'est rattaché à aucun logement — l'incident ne peut pas être créé d'ici.")
      return
    }
    setEnvoi(true); setErreur(null)
    const typeDeg = detail.kind === 'casse' ? 'Casse signalée par le voyageur'
      : detail.kind === 'etat_des_lieux' ? "Constat d'état des lieux d'arrivée"
        : 'Problème signalé par le voyageur'
    let incidentCree = false
    try {
      await createIncident({
        logement_id: detail.logement_id,
        source: 'admin_manual',
        status: STATUT_INCIDENT.EN_COURS,
        degradations: [{
          id: uid(), type: typeDeg, nombre: '1', precision: '',
          statutMenage: 'À vérifier', remplaceLe: '', etatFinancier: null,
          cout: '', photos: [],
        }],
        voyageur: null,
        incident_date: String(detail.submitted_at || '').slice(0, 10) || null,
        label: typeDeg,
        description: detail.description || null,
        reported_by: adminEmail || 'admin',
        photos: [],
      })
      incidentCree = true
      await postAction({ action: 'signalement_voyageur_traiter', famille, id,
        issue: 'incident_ouvert' })
      onFermer(); onDecide()
    } catch (e) {
      setEnvoi(false)
      // ⚠️ DEUX ÉCHECS DIFFÉRENTS, DEUX PHRASES : si l'incident est déjà créé,
      // le dire — sinon l'admin recommence et en crée deux.
      setErreur(incidentCree
        ? phraseRefusSuite(e, "L'incident est créé, mais le signalement n'a pas pu être marqué — il restera dans la liste, ne recréez pas l'incident.")
        : "La création de l'incident n'a pas abouti.")
    }
  }, [envoi, detail, adminEmail, famille, id, onFermer, onDecide])

  /* [D25] SUITE 2 — RÉPONDRE. On n'invente pas un canal : le fil du voyageur
     s'ouvre là où la bulle du signalement est déjà arrivée. Le parent
     (AccueilAdmin) fait l'aiguillage — et si le fil n'est pas dans les
     messages chargés, ça se DIT, plutôt qu'un bouton qui ne fait rien. */
  const repondre = useCallback(() => {
    if (envoi || !detail || !onRepondre) return
    const ouvert = onRepondre(detail)
    if (!ouvert) {
      setErreur("La conversation de ce voyageur n'est pas dans les messages chargés — ouvrez l'onglet Messages pour lui répondre.")
    }
  }, [envoi, detail, onRepondre])

  /* [D25] SUITE 3 — CLASSER SANS SUITE. La ligne sort du bandeau, la base
     GARDE tout : un signalement classé ne disparaît jamais de l'historique
     du séjour. Rien n'est supprimé ici. */
  const classer = useCallback(async () => {
    if (envoi) return
    setEnvoi(true); setErreur(null)
    try {
      await postAction({ action: 'signalement_voyageur_traiter', famille, id,
        issue: 'classe_sans_suite' })
      onFermer(); onDecide()
    } catch (e) {
      setEnvoi(false)
      setErreur(phraseRefusSuite(e, "Le classement n'a pas abouti."))
    }
  }, [envoi, famille, id, onFermer, onDecide])

  const supprimer = useCallback(async () => {
    if (envoi) return
    setEnvoi(true); setErreur(null)
    try {
      await postAction({ action: 'signalement_voyageur_supprimer', famille, id })
      onFermer(); onDecide()
    } catch (e) {
      setEnvoi(false)
      setErreur("La suppression n'a pas abouti.")
    }
  }, [envoi, famille, id, onFermer, onDecide])

  const photos = (detail && Array.isArray(detail.photos)) ? detail.photos : []
  const signees = photos.filter((p) => p && p.signed_url)
  const perdues = photos.length - signees.length

  return (
    <Feuille testId="accueil-feuille-signalement"
      titre={titre || 'Un voyageur nous a écrit'} suffixe={sousTitre}
      onFermer={onFermer}
      enfants={
        <>
          {etat === 'lecture' && <p className="note">On lit…</p>}

          {etat === 'absent' && (
            <p className="note" data-testid="signalement-inerte">
              {erreur || "Ce signalement n'existe plus."}
            </p>
          )}

          {etat === 'echec' && (
            <p className="dang" role="alert" data-testid="signalement-echec">
              {erreur}
            </p>
          )}

          {etat === 'pret' && detail && (
            <>
              <div className="info" data-testid="signalement-texte"
                style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                {detail.description || 'Aucun texte — seulement des photos.'}
              </div>

              {photos.length > 0 && (
                <div style={{ marginTop: 12 }}>
                  <p className="note">
                    {photos.length} photo{photos.length > 1 ? 's' : ''}
                    {/* ⚠️ UNE PHOTO QUI MANQUE SE DIT. Un objet purgé ou
                        introuvable n'est pas masqué : l'écran l'annonce. */}
                    {perdues > 0
                      ? ` · ${perdues} introuvable${perdues > 1 ? 's' : ''} en archive`
                      : ''}
                  </p>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginTop: 6 }}>
                    {signees.map((p, i) => (
                      <button key={p.signed_url} type="button"
                        data-testid={`signalement-photo-${i}`}
                        onClick={() => onPhoto && onPhoto(p.signed_url)}
                        style={{ padding: 0, border: 'none', background: 'none',
                          cursor: 'pointer', minHeight: 44 }}>
                        <img src={p.signed_url} alt={`Ce que le voyageur a joint (${i + 1})`}
                          style={{ width: 86, height: 86, objectFit: 'cover',
                            borderRadius: 8, display: 'block' }} />
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {/* [D25] LES TROIS SUITES — et ce que chacune fait, dit AVANT le
                  clic. Aucune ne supprime : classer sans suite laisse le
                  signalement lisible dans l'historique du séjour. */}
              <p className="note" style={{ marginTop: 12 }}>
                Trois suites possibles : ouvrir un incident (il se qualifie
                dans le chantier des incidents — rien ne se chiffre ici),
                répondre dans la conversation du voyageur (le signalement y
                est déjà arrivé), ou classer sans suite — le signalement
                reste alors dans l'historique du séjour, il ne disparaît
                jamais.
              </p>

              <div className="act">
                <button className="bt bt-p" data-testid="signalement-incident"
                  onClick={ouvrirIncident} disabled={envoi}>
                  {envoi ? 'On enregistre…' : 'Ouvrir un incident'}
                </button>
                <button className="bt bt-s" data-testid="signalement-repondre"
                  onClick={repondre} disabled={envoi}>
                  Répondre
                </button>
                <button className="bt bt-s" data-testid="signalement-classer"
                  onClick={classer} disabled={envoi}>
                  Classer sans suite
                </button>
                <button className="bt bt-s" onClick={onFermer}>Fermer</button>
              </div>

              {/* LA SUPPRESSION DÉFINITIVE — repliée derrière une confirmation :
                  elle efface pour de bon, texte et photos. */}
              <div style={{ marginTop: 14 }}>
                {!confirmeSuppr ? (
                  <button type="button" className="bt bt-s"
                    data-testid="signalement-supprimer"
                    onClick={() => setConfirmeSuppr(true)} disabled={envoi}>
                    Supprimer définitivement
                  </button>
                ) : (
                  <>
                    <p className="note">
                      Le texte et les photos seront effacés pour de bon. Il
                      restera seulement la trace de qui a supprimé, et quand —
                      jamais le contenu.
                    </p>
                    <div className="act">
                      <button className="bt bt-p"
                        data-testid="signalement-supprimer-confirmer"
                        onClick={supprimer} disabled={envoi}>
                        {envoi ? 'On efface…' : 'Effacer définitivement'}
                      </button>
                      <button className="bt bt-s"
                        onClick={() => setConfirmeSuppr(false)} disabled={envoi}>
                        Annuler
                      </button>
                    </div>
                  </>
                )}
              </div>

              <Erreur texte={erreur} />
            </>
          )}
        </>
      } />
  )
}
